import {
  INBOX_LIST_ID,
  TRASH_RETENTION_DAYS,
  type GlobalTag,
  type Item,
  type List,
  type ListTag,
  type Selection,
  type View,
} from '../db/types';
import {
  calendarGrid,
  dayKey,
  dayOffset,
  formatFull,
  formatMonthYear,
  isOverdue,
  isToday,
  notStartedYet,
  parseMonthKey,
  sameDay,
  thisMonthKey,
  upcomingGroup,
} from './dates';

export interface Section {
  key: string;
  label: string;
  /** Lighter text beside the label — the concrete date behind a word like "Tomorrow". */
  sub?: string;
  tone?: 'overdue';
  items: Item[];
}

export interface Catalog {
  lists: List[];
  listTags: ListTag[];
  globalTags: GlobalTag[];
}

const live = (i: Item) => !i.deleted;
const tasks = (items: Item[]) => items.filter((i) => i.kind === 'todo');
const notes = (items: Item[]) => items.filter((i) => i.kind === 'note');

/** Open work first, then completed; within each, priority then due date. */
function byUrgency(a: Item, b: Item) {
  if (a.done !== b.done) return a.done ? 1 : -1;
  if (a.priority !== b.priority) return a.priority - b.priority;
  const at = a.due_at ? Date.parse(a.due_at) : Infinity;
  const bt = b.due_at ? Date.parse(b.due_at) : Infinity;
  if (at !== bt) return at - bt;
  return a.sort_order - b.sort_order;
}

const byDue = (a: Item, b: Item) =>
  Date.parse(a.due_at ?? '') - Date.parse(b.due_at ?? '') || a.priority - b.priority;

const byModified = (a: Item, b: Item) => b.updated_at.localeCompare(a.updated_at);

/**
 * The day a task shows up under in Upcoming: the day it becomes actionable.
 * A task that starts Wednesday and is due a fortnight later belongs on
 * Wednesday — that is the whole reason start and due are separate fields.
 */
function upcomingAnchor(item: Item): string | null {
  if (item.start_at && notStartedYet(item.start_at)) return item.start_at;
  return item.due_at ?? item.start_at;
}

/**
 * The day an item occupies on the calendar.
 *
 * A task is easy — it belongs on its deadline. A note usually has no date at
 * all, and falling back to `created_at` is what makes the calendar useful on
 * the day it ships rather than after a month of manual dating: every note
 * appears on the day it was written, so the month reads as a journal. Setting
 * a date on a note in the editor moves it, because `due_at` wins.
 *
 * Deliberately not a new column. `created_at` already records this, and a
 * second date field would mean a Dexie version, a Postgres migration and a
 * sync payload change to store something the row already knows.
 */
export function calendarAnchor(item: Item): string | null {
  if (item.kind === 'todo') return item.due_at ?? item.start_at;
  return item.due_at ?? item.created_at;
}

export interface CalendarDay {
  /** `YYYY-MM-DD`. */
  key: string;
  date: Date;
  /** False for the neighbouring-month days that pad the 6×7 grid. */
  inMonth: boolean;
  isToday: boolean;
  items: Item[];
}

/** Open work first, then notes by recency, then anything already done. */
function byDayOrder(a: Item, b: Item) {
  if (a.done !== b.done) return a.done ? 1 : -1;
  if (a.kind !== b.kind) return a.kind === 'todo' ? -1 : 1;
  if (a.kind === 'todo' && b.kind === 'todo') {
    if (a.priority !== b.priority) return a.priority - b.priority;
    const at = a.due_at ? Date.parse(a.due_at) : Infinity;
    const bt = b.due_at ? Date.parse(b.due_at) : Infinity;
    if (at !== bt) return at - bt;
  }
  return b.updated_at.localeCompare(a.updated_at);
}

/**
 * The 42 cells a month renders, each carrying the items anchored to it.
 *
 * `items` is expected to be already filtered — the stacked global-tag filters
 * narrow a calendar exactly like they narrow a list.
 */
export function calendarDays(month: string, items: Item[]): CalendarDay[] {
  const first = parseMonthKey(month);
  const today = new Date();

  const buckets = new Map<string, Item[]>();
  for (const item of items) {
    if (item.deleted) continue;
    const anchor = calendarAnchor(item);
    if (!anchor) continue;
    const key = dayKey(anchor);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(item);
    else buckets.set(key, [item]);
  }

  return calendarGrid(first).map((date) => {
    const key = dayKey(date);
    return {
      key,
      date,
      inMonth: date.getMonth() === first.getMonth(),
      isToday: sameDay(date, today),
      items: (buckets.get(key) ?? []).sort(byDayOrder),
    };
  });
}

/** The header's running total, counting only days that belong to the month. */
export function calendarTotals(days: CalendarDay[]) {
  let notes = 0;
  let tasks = 0;
  for (const day of days) {
    if (!day.inMonth) continue;
    for (const item of day.items) {
      if (item.kind === 'note') notes += 1;
      else tasks += 1;
    }
  }
  return { notes, tasks };
}

/* ── Selection → items ───────────────────────────────────── */

/**
 * Items matching the sidebar selection alone, before any stacked filters.
 *
 * A list tag only ever narrows within its own list, so selecting one implies
 * its parent list — the two can never disagree.
 */
export function itemsForSelection(selection: Selection, all: Item[]): Item[] {
  if (selection.type === 'trash') return all.filter((i) => i.deleted);
  const items = all.filter(live);

  switch (selection.type) {
    case 'today': {
      const t = tasks(items).filter(
        (i) =>
          !notStartedYet(i.start_at) &&
          (isOverdue(i.due_at, i.done) || isToday(i.due_at) || (i.done && isToday(i.completed_at))),
      );
      return [...t, ...notes(items).filter((i) => isToday(i.updated_at))];
    }

    case 'upcoming':
      return tasks(items).filter((i) => {
        if (i.done) return false;
        const anchor = upcomingAnchor(i);
        if (!anchor) return false;
        const off = dayOffset(anchor);
        return off > 0 && off <= 14;
      });

    // Everything the 6×7 grid can show, padding days included — the pane
    // buckets them by day, and the stacked filters in `itemsFor` still get to
    // narrow the set before it ever reaches the grid.
    case 'calendar': {
      const grid = calendarGrid(parseMonthKey(selection.month));
      const from = dayKey(grid[0]!);
      const to = dayKey(grid[grid.length - 1]!);
      return items.filter((i) => {
        const anchor = calendarAnchor(i);
        if (!anchor) return false;
        const key = dayKey(anchor);
        return key >= from && key <= to;
      });
    }

    case 'inbox':
      return items.filter((i) => i.list_id === INBOX_LIST_ID);

    case 'all':
      return items;

    case 'list':
      return items.filter((i) => i.list_id === selection.listId);

    case 'listTag':
      return items.filter(
        (i) => i.list_id === selection.listId && i.list_tag_ids.includes(selection.tagId),
      );

    case 'globalTag':
      return items.filter((i) => i.global_tag_ids.includes(selection.tagId));

    default:
      return items;
  }
}

/** The selection, narrowed by every stacked global-tag filter. AND, not OR. */
export function itemsFor(view: View, all: Item[]): Item[] {
  let items = itemsForSelection(view.selection, all);

  for (const tagId of view.globalTagIds) {
    items = items.filter((i) => i.global_tag_ids.includes(tagId));
  }
  return items;
}

/* ── Grouping ────────────────────────────────────────────── */

export function sectionsFor(view: View, all: Item[]): Section[] {
  const items = itemsFor(view, all);

  switch (view.selection.type) {
    case 'today': {
      const t = tasks(items);
      return compact([
        {
          key: 'overdue',
          label: 'Overdue',
          tone: 'overdue' as const,
          items: t.filter((i) => isOverdue(i.due_at, i.done)).sort(byDue),
        },
        {
          key: 'today',
          label: 'Today',
          items: t.filter((i) => !isOverdue(i.due_at, i.done)).sort(byUrgency),
        },
        { key: 'notes', label: 'Notes edited today', items: notes(items).sort(byModified) },
      ]);
    }

    case 'upcoming': {
      const groups = new Map<string, Section>();
      for (const item of items) {
        const g = upcomingGroup(upcomingAnchor(item)!);
        const section = groups.get(g.key) ?? { key: g.key, label: g.label, sub: g.sub, items: [] };
        section.items.push(item);
        groups.set(g.key, section);
      }
      for (const section of groups.values()) section.items.sort(byUrgency);
      return [...groups.values()].sort((a, b) => a.key.localeCompare(b.key));
    }

    // The calendar is its own layout, not a stack of sections. Returning none
    // is also what switches the list pane's j/k row-stepping off, which is
    // right: in a grid the arrow keys have to move by day.
    case 'calendar':
      return [];

    case 'trash':
      return compact([{ key: 'trash', label: 'Deleted', items: [...items].sort(byModified) }]);

    default:
      return compact([
        { key: 'notes', label: 'Notes', items: notes(items).sort(byModified) },
        { key: 'tasks', label: 'Tasks', items: tasks(items).sort(byUrgency) },
      ]);
  }
}

const compact = (sections: Section[]) => sections.filter((s) => s.items.length > 0);

/* ── Headings ────────────────────────────────────────────── */

export interface ViewMeta {
  title: string;
  /** Right-aligned text in the list pane header. */
  detail: string;
}

export function viewMeta(view: View, catalog: Catalog, count: number): ViewMeta {
  const { selection } = view;

  switch (selection.type) {
    case 'today':
      return { title: 'Today', detail: formatFull(new Date().toISOString()) };
    case 'upcoming':
      return { title: 'Upcoming', detail: 'next 14 days' };
    case 'calendar':
      return { title: formatMonthYear(parseMonthKey(selection.month)), detail: '' };
    case 'inbox':
      return { title: 'Inbox', detail: `${count} items` };
    case 'all':
      return { title: 'All Notes', detail: `${count} items` };
    case 'trash':
      return { title: 'Trash', detail: `${TRASH_RETENTION_DAYS}-day retention` };
    case 'conflicts':
      return { title: 'Conflict log', detail: 'local only' };
    case 'settings':
      return { title: 'Settings', detail: '' };
    case 'list':
      return {
        title: catalog.lists.find((l) => l.id === selection.listId)?.name ?? 'List',
        detail: `${count} items`,
      };
    case 'listTag': {
      const list = catalog.lists.find((l) => l.id === selection.listId);
      const tag = catalog.listTags.find((t) => t.id === selection.tagId);
      return { title: tag?.name ?? 'Tag', detail: list ? `in ${list.name}` : `${count} items` };
    }
    case 'globalTag':
      return {
        title: catalog.globalTags.find((t) => t.id === selection.tagId)?.name ?? 'Tag',
        detail: `${count} items · all lists`,
      };
  }
}

/* ── Counts ──────────────────────────────────────────────── */

export interface Counts {
  today: number;
  upcoming: number;
  /** Everything anchored inside the current real month, not the month on screen. */
  calendar: number;
  inbox: number;
  all: number;
  trash: number;
  lists: Record<string, number>;
  listTags: Record<string, number>;
  globalTags: Record<string, number>;
}

/** Sidebar counts. Cheap enough to recompute whenever items change. */
export function viewCounts(all: Item[], catalog: Catalog): Counts {
  const items = all.filter(live);
  const openTasks = (list: Item[]) => list.filter((i) => i.kind === 'todo' && !i.done).length;

  const thisMonth = calendarTotals(calendarDays(thisMonthKey(), items));

  const lists: Record<string, number> = {};
  const listTags: Record<string, number> = {};
  const globalTags: Record<string, number> = {};

  for (const list of catalog.lists) lists[list.id] = 0;
  for (const tag of catalog.listTags) listTags[tag.id] = 0;
  for (const tag of catalog.globalTags) globalTags[tag.id] = 0;

  for (const item of items) {
    lists[item.list_id] = (lists[item.list_id] ?? 0) + 1;
    for (const id of item.list_tag_ids) listTags[id] = (listTags[id] ?? 0) + 1;
    for (const id of item.global_tag_ids) globalTags[id] = (globalTags[id] ?? 0) + 1;
  }

  return {
    today: openTasks(itemsForSelection({ type: 'today' }, all)),
    upcoming: itemsForSelection({ type: 'upcoming' }, all).length,
    // The sidebar badge answers "how much is in this month", so it counts the
    // month itself — not the 42-cell grid, which bleeds into its neighbours.
    calendar: thisMonth.notes + thisMonth.tasks,
    inbox: openTasks(itemsForSelection({ type: 'inbox' }, all)),
    all: items.length,
    trash: all.filter((i) => i.deleted).length,
    lists,
    listTags,
    globalTags,
  };
}
